/**
 * Charts for the dashboard.
 *
 * Rules held to throughout: one y-scale per chart (never dual axis), fixed
 * categorical colour order, single-hue sequential for pure magnitude, a legend
 * whenever there are two or more series, direct labels so identity is never
 * carried by colour alone, and a tooltip on every plotted form.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatINR, formatPoints, seriesColor } from "../lib/format";

const GRID = "#e6e9ef";
const AXIS_TEXT = "#647694";

const axisProps = {
  tick: { fill: AXIS_TEXT, fontSize: 11 },
  tickLine: false,
  axisLine: { stroke: GRID },
} as const;

// Recharts replays its entry animation whenever ResponsiveContainer re-measures
// (any window resize), which flickers on a dashboard. Draw the marks outright.
const STATIC = { isAnimationActive: false } as const;

function TooltipBox({ rows, title }: { title: string; rows: { label: string; value: string; color?: string }[] }) {
  return (
    <div className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="mb-1.5 font-semibold text-ink-900">{title}</div>
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-2 py-0.5">
          {r.color && <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: r.color }} />}
          <span className="text-ink-500">{r.label}</span>
          <span className="tnum ml-auto font-medium text-ink-900">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

/** Monthly spend — magnitude over time, one series, so no legend needed. */
export function MonthlySpendChart({ data }: { data: { label: string; spend: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="label" {...axisProps} minTickGap={14} />
        <YAxis {...axisProps} width={56} tickFormatter={(v: number) => formatINR(v, { compact: true })} />
        <Tooltip
          cursor={{ fill: "#f6f7f9" }}
          content={({ active, payload, label }) =>
            active && payload?.length ? (
              <TooltipBox
                title={String(label)}
                rows={[{ label: "Spend", value: formatINR(Number(payload[0].value)), color: seriesColor(0) }]}
              />
            ) : null
          }
        />
        <Bar dataKey="spend" fill={seriesColor(0)} radius={[4, 4, 0, 0]} maxBarSize={34} {...STATIC} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Reward value earned per month. Kept as its own chart rather than sharing an
 * axis with spend — two measures of different scale never belong on one plot.
 */
export function RewardValueChart({ data }: { data: { label: string; value: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="label" {...axisProps} minTickGap={14} />
        <YAxis {...axisProps} width={56} tickFormatter={(v: number) => formatINR(v, { compact: true })} />
        <Tooltip
          cursor={{ stroke: GRID, strokeWidth: 1 }}
          content={({ active, payload, label }) =>
            active && payload?.length ? (
              <TooltipBox
                title={String(label)}
                rows={[{ label: "Reward value", value: formatINR(Number(payload[0].value)), color: seriesColor(2) }]}
              />
            ) : null
          }
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke={seriesColor(2)}
          strokeWidth={2}
          dot={{ r: 3, fill: seriesColor(2), strokeWidth: 0 }}
          activeDot={{ r: 5, stroke: "#ffffff", strokeWidth: 2 }}
          {...STATIC}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Spend by category — pure magnitude, so a single sequential hue, darkest for
 * the largest. Categories are named on the axis, so colour carries no identity.
 */
export function CategoryBreakdownChart({
  data,
}: {
  data: { label: string; spend: number; points: number }[];
}) {
  const top = data.slice(0, 8);
  const max = Math.max(...top.map((d) => d.spend), 1);
  const ramp = ["#1c5cab", "#256abf", "#2a78d6", "#3987e5", "#5598e7", "#6da7ec", "#86b6ef", "#9ec5f4"];

  return (
    <ResponsiveContainer width="100%" height={Math.max(200, top.length * 38)}>
      <BarChart data={top} layout="vertical" margin={{ top: 4, right: 56, left: 0, bottom: 4 }}>
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis type="number" {...axisProps} tickFormatter={(v: number) => formatINR(v, { compact: true })} />
        <YAxis type="category" dataKey="label" {...axisProps} width={150} />
        <Tooltip
          cursor={{ fill: "#f6f7f9" }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <TooltipBox
                title={String(payload[0].payload.label)}
                rows={[
                  { label: "Spend", value: formatINR(payload[0].payload.spend) },
                  { label: "Points", value: formatPoints(payload[0].payload.points) },
                ]}
              />
            ) : null
          }
        />
        <Bar dataKey="spend" radius={[0, 4, 4, 0]} maxBarSize={22} {...STATIC}>
          {top.map((d) => (
            <Cell key={d.label} fill={ramp[Math.min(ramp.length - 1, Math.round((1 - d.spend / max) * (ramp.length - 1)))]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Reward value per card. Each card keeps its own palette slot everywhere in the
 * app, and the card name is on the axis, so identity never rests on colour.
 */
export function CardValueChart({
  data,
}: {
  data: { cardName: string; value: number; spend: number; color: string; returnPct: number }[];
}) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, data.length * 42)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, left: 0, bottom: 4 }}>
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis type="number" {...axisProps} tickFormatter={(v: number) => formatINR(v, { compact: true })} />
        <YAxis type="category" dataKey="cardName" {...axisProps} width={150} />
        <Tooltip
          cursor={{ fill: "#f6f7f9" }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <TooltipBox
                title={String(payload[0].payload.cardName)}
                rows={[
                  { label: "Reward value", value: formatINR(payload[0].payload.value), color: payload[0].payload.color },
                  { label: "Spend", value: formatINR(payload[0].payload.spend) },
                  { label: "Return", value: `${payload[0].payload.returnPct.toFixed(2)}%` },
                ]}
              />
            ) : null
          }
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={24} {...STATIC}>
          {data.map((d) => (
            <Cell key={d.cardName} fill={d.color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Potential value per transfer programme.
 *
 * Miles are charted as rupees rather than raw counts: a Hilton point and an
 * Accor point are not the same thing, so comparing counts across programmes
 * would be meaningless. Magnitude, so a single hue, and the programme names
 * sit on the axis — colour carries no identity here.
 */
export function PartnerPotentialChart({
  data,
}: {
  data: { partnerName: string; potentialValue: number; potentialUnits: number; balanceUnits: number }[];
}) {
  const top = data.slice(0, 10);
  const max = Math.max(...top.map((d) => d.potentialValue), 1);
  const ramp = ["#1c5cab", "#256abf", "#2a78d6", "#3987e5", "#5598e7", "#6da7ec", "#86b6ef", "#9ec5f4", "#b7d3f6", "#cde2fb"];

  return (
    <ResponsiveContainer width="100%" height={Math.max(220, top.length * 38)}>
      <BarChart data={top} layout="vertical" margin={{ top: 4, right: 56, left: 0, bottom: 4 }}>
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis type="number" {...axisProps} tickFormatter={(v: number) => formatINR(v, { compact: true })} />
        <YAxis type="category" dataKey="partnerName" {...axisProps} width={190} />
        <Tooltip
          cursor={{ fill: "#f6f7f9" }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <TooltipBox
                title={String(payload[0].payload.partnerName)}
                rows={[
                  { label: "Potential value", value: formatINR(payload[0].payload.potentialValue) },
                  { label: "Potential miles", value: formatPoints(payload[0].payload.potentialUnits) },
                  { label: "Held today", value: formatPoints(payload[0].payload.balanceUnits) },
                ]}
              />
            ) : null
          }
        />
        <Bar dataKey="potentialValue" radius={[0, 4, 4, 0]} maxBarSize={22} {...STATIC}>
          {top.map((d) => (
            <Cell
              key={d.partnerName}
              fill={ramp[Math.min(ramp.length - 1, Math.round((1 - d.potentialValue / max) * (ramp.length - 1)))]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
